import type { CompetitionStandings } from '@/lib/firebase/queries/competitionStandings';
import type { ContainerStandingRow } from '@/lib/competitions/competitionRanking';
import { riyadhDateKey, type CompetitionWindow } from '@/lib/competitions/competitionDates';

export interface CompetitionCounts {
  /** Every active beneficiary of the organization (CONTEXT.md: Participant). */
  participants: number;
  /** Participants with at least one completed habit inside the current dates. */
  activeParticipants: number;
  /** Completed habit logs inside the current dates. */
  completions: number;
  groups: number;
  sections: number;
}

/**
 * The counts on a competition's read-only details view (namat#26), taken
 * from the same standings the results dialog and beneficiaries use, so they
 * follow the competition's current start/end days (namat#34).
 */
export function competitionCounts(standings: Pick<CompetitionStandings, 'individuals' | 'groups' | 'sections'>): CompetitionCounts {
  return {
    participants: standings.individuals.length,
    activeParticipants: standings.individuals.filter(p => p.completed > 0).length,
    completions: standings.individuals.reduce((sum, p) => sum + p.completed, 0),
    groups: standings.groups.length,
    sections: standings.sections.length,
  };
}

// ── Beneficiary details dialog (namat#36, mobile CompetitionDetailsSheet) ──
// Same shapes and rules as namat-mobile src/lib/competitions/competitionDetails.ts
// (namat-mobile#14). Ranking is not redefined here: it comes ranked from
// loadCompetitionStandings (the rule shared with mobile and the server).

/** How many rows the details dialog lists per ranking («أفضل 10»). */
export const DETAILS_TOP_N = 10;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface PerUserCompletion {
  userId: string;
  completed: number;
}

/** The first `topN` rows, plus the viewer's own row even when it falls outside them. */
export function topWithMe<T extends { isMe: boolean }>(
  ranking: T[],
  topN: number
): { rows: T[]; me: T | null; total: number } {
  return {
    rows: ranking.slice(0, topN),
    me: ranking.find((r) => r.isMe) ?? null,
    total: ranking.length,
  };
}

export type CompetitionPhase = 'upcoming' | 'running' | 'ended';

export interface CompetitionStats {
  /** Every beneficiary taking part (zero rows included, namat#29). */
  participants: number;
  /** Participants with at least one completed habit in the window. */
  activeParticipants: number;
  /** All completed habits in the window, everyone together. */
  totalCompleted: number;
  phase: CompetitionPhase;
  /** Riyadh days left including today; 0 once ended. */
  daysLeft: number;
  totalDays: number;
}

const dayNumber = (key: string): number => Math.round(Date.parse(`${key}T00:00:00Z`) / DAY_MS);

/** Headline numbers for a competition, with days counted on the Riyadh calendar like the logs. */
export function competitionStats(
  perUser: PerUserCompletion[],
  window: CompetitionWindow,
  now: Date
): CompetitionStats {
  const start = dayNumber(riyadhDateKey(window.startDate));
  const end = dayNumber(riyadhDateKey(window.endDate));
  const today = dayNumber(riyadhDateKey(now));
  const phase: CompetitionPhase = now > window.endDate ? 'ended' : now < window.startDate ? 'upcoming' : 'running';
  const totalDays = Math.max(1, end - start + 1);
  const daysLeft = phase === 'ended' ? 0 : phase === 'upcoming' ? totalDays : Math.max(1, end - today + 1);
  return {
    participants: perUser.length,
    activeParticipants: perUser.filter((r) => r.completed > 0).length,
    totalCompleted: perUser.reduce((sum, r) => sum + r.completed, 0),
    phase,
    daysLeft,
    totalDays,
  };
}

export interface IndividualRow {
  userId: string;
  completed: number;
  rank: number;
  isMe: boolean;
}

export interface RankingSlice<T> {
  rows: T[];
  me: T | null;
  total: number;
}

export interface CompetitionDetails {
  individuals: RankingSlice<IndividualRow>;
  groups: RankingSlice<ContainerStandingRow>;
  /** null when the organization has no sections at all — the tab is hidden. */
  sections: RankingSlice<ContainerStandingRow> | null;
  stats: CompetitionStats;
}

/**
 * Everything the beneficiary details dialog shows, from one standings read.
 * `currentUserId` is '' for a viewer who is not a participant: no row is "me".
 */
export function buildCompetitionDetails(input: {
  currentUserId: string;
  standings: Pick<CompetitionStandings, 'individuals' | 'groups' | 'sections'>;
  window: CompetitionWindow;
  now: Date;
  topN?: number;
}): CompetitionDetails {
  const { currentUserId, standings, window, now } = input;
  const topN = input.topN ?? DETAILS_TOP_N;
  const individuals = standings.individuals.map((r) => ({
    userId: r.userId,
    completed: r.completed,
    rank: r.rank,
    isMe: !!currentUserId && r.userId === currentUserId,
  }));
  return {
    individuals: topWithMe(individuals, topN),
    groups: topWithMe(standings.groups, topN),
    sections: standings.sections.length > 0 ? topWithMe(standings.sections, topN) : null,
    stats: competitionStats(standings.individuals, window, now),
  };
}
